import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import InsightToggleView from '../insight/InsightToggleView';
import { hasRenderableVisual } from '../insight/util';
import type { VisualPayload } from '../insight/types';

/**
 * Presentational, read-only render of an hcv2 chat transcript. Takes the messages array and nothing
 * else — no fetching, auth, routing, polling or live-session state — so it can be dropped into any
 * surface that already has the messages (the shared-chat page, and the RLHF rating workbench).
 *
 * A stored assistant message nests its visual/format under `metadata` (the shape persisted by the
 * orchestrator and returned by /shared/{token} and /sessions/{id}), while a live in-memory message
 * puts `visual` at the top level. Refusal/clarification/error messages carry no `metadata` at all.
 * `visualOf` normalizes all three so the renderer never assumes a shape.
 */

export interface ReadOnlyChatMessage {
  role: 'user' | 'assistant';
  content: string;
  visual?: VisualPayload | null;
  metadata?: { visual?: VisualPayload | null } | null;
}

const markdownComponents: Components = {
  h1: ({ children }) => <h1 className="text-xl font-bold text-[#1C1C1E] mt-4 mb-2">{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-bold text-[#1C1C1E] mt-4 mb-2">{children}</h2>,
  h3: ({ children }) => (
    <h3 className="text-base font-semibold text-[#1C1C1E] mt-3 mb-1">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-sm font-semibold text-[#1C1C1E] mt-2 mb-1">{children}</h4>
  ),
  p: ({ children }) => <p className="text-sm text-[#374151] mb-3 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc list-outside ml-5 mb-3 space-y-1">{children}</ul>,
  ol: ({ children }) => (
    <ol className="list-decimal list-outside ml-5 mb-3 space-y-1">{children}</ol>
  ),
  li: ({ children }) => <li className="text-sm text-[#374151] leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-[#1C1C1E]">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-[#07E5D1] pl-4 py-1 my-3 bg-[#FAFBFC] rounded-r-lg">
      {children}
    </blockquote>
  ),
  code: ({ children, className }) => {
    const isInline = !className;
    return isInline ? (
      <code className="bg-[#FAFBFC] text-[#374151] px-1.5 py-0.5 rounded text-xs font-mono">
        {children}
      </code>
    ) : (
      <code className="block bg-gray-900 text-gray-100 p-3 rounded-lg text-xs font-mono overflow-x-auto my-3">
        {children}
      </code>
    );
  },
  pre: ({ children }) => <pre className="my-3">{children}</pre>,
  a: ({ href, children }) => (
    <a
      href={href}
      className="text-[#07E5D1] hover:underline"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  hr: () => <hr className="my-4 border-[#E5E7EB]" />,
  table: ({ children }) => (
    <div className="overflow-x-auto my-4 rounded-lg border border-[#E5E7EB]">
      <table className="min-w-full text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-[#FAFBFC] border-b border-[#E5E7EB]">{children}</thead>
  ),
  tbody: ({ children }) => <tbody className="divide-y divide-[#F1F5F9]">{children}</tbody>,
  tr: ({ children }) => <tr className="hover:bg-[#FAFBFC] transition-colors">{children}</tr>,
  th: ({ children }) => (
    <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#374151] uppercase tracking-wider">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-4 py-2.5 text-sm text-[#374151] align-top">{children}</td>
  ),
};

/** The visual can live at top-level (live messages) or under metadata (stored messages). */
const visualOf = (msg: ReadOnlyChatMessage): VisualPayload | null | undefined =>
  msg.visual ?? msg.metadata?.visual;

interface ReadOnlyChatViewProps {
  messages: ReadOnlyChatMessage[];
  /** Optional heading shown above the transcript (e.g. the session title). */
  title?: string | null;
}

const ReadOnlyChatView: React.FC<ReadOnlyChatViewProps> = ({ messages, title }) => {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const handleCopy = (content: string, idx: number) => {
    void navigator.clipboard?.writeText(content);
    setCopiedIndex(idx);
    window.setTimeout(() => setCopiedIndex((cur) => (cur === idx ? null : cur)), 1500);
  };

  if (!messages || messages.length === 0) {
    return (
      <div className="px-6 py-10 text-center text-sm text-[#9CA3AF]">
        This conversation has no messages.
      </div>
    );
  }

  return (
    <div className="max-w-[960px] mx-auto px-6 py-6">
      {title && <h2 className="text-base font-semibold text-[#1C1C1E] mb-4">{title}</h2>}
      <div className="space-y-5">
        {messages.map((msg, idx) => {
          const visual = visualOf(msg);
          return (
            <div
              key={idx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`rounded-2xl px-5 py-4 ${
                  msg.role === 'user'
                    ? 'max-w-[85%] bg-[#07E5D1] text-white rounded-br-md'
                    : `bg-white border border-[#E5E7EB] text-[#374151] rounded-bl-md shadow-sm ${
                        hasRenderableVisual(visual) ? 'w-full' : 'max-w-full'
                      }`
                }`}
              >
                {msg.role === 'assistant' ? (
                  <div className="max-w-none">
                    {hasRenderableVisual(visual) ? (
                      <InsightToggleView content={msg.content} visual={visual} />
                    ) : (
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                        {msg.content}
                      </ReactMarkdown>
                    )}
                    <div className="flex justify-end mt-3 pt-2 border-t border-[#F1F5F9]">
                      <button
                        onClick={() => handleCopy(msg.content, idx)}
                        className={`flex items-center gap-1.5 px-2 py-1 text-xs transition-colors rounded ${
                          copiedIndex === idx
                            ? 'text-[#10B981] bg-[#ECFDF5]'
                            : 'text-[#9CA3AF] hover:text-[#07E5D1] hover:bg-[#FAFBFC]'
                        }`}
                        title="Copy to clipboard"
                      >
                        {copiedIndex === idx ? (
                          <>
                            <Check className="w-3.5 h-3.5" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="w-3.5 h-3.5" />
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReadOnlyChatView;
